import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import classNames from 'classnames';
import styled from 'styled-components';
import { CLASSNAME_PREFIXES } from '../../constants/classname_prefixes.js';
import { useTranslations } from '../../hooks/use_translations.js';
import { CTAColors, neutralColors } from '../../styles/colors/colors.js';
import { useThemeContext } from '../../styles/themes/themes.js';
import { avatarSizeToPixelsMap, AvatarUser } from '../AvatarUser/AvatarUser.js';
import { circleSizeMap } from '../Circle/Circle.js';
import { Tooltip } from '../Tooltip/Tooltip.js';
import { jsx } from 'react/jsx-runtime';

var _excluded = ["className", "onClick", "size", "tooltipProps", "users", "variant"];
var MAX_USER_AVATARS = {
  default: 5,
  skinny: 2
};
var MAX_USERNAMES_IN_TOOLTIP = {
  default: 3,
  skinny: 2
};
var ClusterWrapper = styled.div.withConfig({
  displayName: "AvatarCluster__ClusterWrapper",
  componentId: "sc-1kgyhon-0"
})(["&&{display:flex;flex-direction:row;padding:0;border-radius:100%;border:none;background:none;cursor:", ";}&& :focus{outline:2px solid ", ";}"], function (props) {
  return props.as === "button" ? "pointer" : "default";
}, CTAColors.primaryVariant);
var AvatarUserContainer = styled.div.withConfig({
  displayName: "AvatarCluster__AvatarUserContainer",
  componentId: "sc-1kgyhon-1"
})(["&&{", ";}"], function (_ref) {
  var avatarSize = _ref.avatarSize;
  var size = circleSizeMap[avatarSize];
  return "margin-inline-start: -".concat(size * 0.2, "px;\n        :first-child {\n            margin-inline-start: 0;\n        }");
});
var CountContainer = styled.div.withConfig({
  displayName: "AvatarCluster__CountContainer",
  componentId: "sc-1kgyhon-2"
})(["&&{display:flex;align-items:center;margin-inline-start:5px;font-weight:600;color:", ";", ";}"], neutralColors.charcoal, function (_ref2) {
  var size = _ref2.size;
  return "\n        height: ".concat(circleSizeMap[size], "px;\n        font-size: ").concat(avatarSizeToPixelsMap[size].fontSize, "px;\n    ");
});
var getTooltipContent = function getTooltipContent(userNames, t) {
  var translatedString;
  switch (userNames.length) {
    case 1:
      translatedString = t("AvatarCluster.tooltipContentForSingleUser", {
        user: userNames[0]
      });
      break;
    case 2:
      translatedString = t("AvatarCluster.tooltipContentForTwoUsers", {
        firstUser: userNames[0],
        secondUser: userNames[1]
      });
      break;
    default:
      translatedString = t("AvatarCluster.defaultTooltipContent", {
        firstUser: userNames[0],
        secondUser: userNames[1],
        thirdUser: userNames[2]
      });
  }
  return translatedString;
};
var AvatarCluster = function AvatarCluster(props) {
  var className = props.className,
    onClick = props.onClick,
    _props$size = props.size,
    size = _props$size === void 0 ? "lg" : _props$size,
    tooltipProps = props.tooltipProps,
    users = props.users,
    _props$variant = props.variant,
    variant = _props$variant === void 0 ? "default" : _props$variant,
    rest = _objectWithoutProperties(props, _excluded);
  var _useThemeContext = useThemeContext(),
    localization = _useThemeContext.localization;
  var _useTranslations = useTranslations(localization === null || localization === void 0 ? void 0 : localization.translations),
    t = _useTranslations.t;
  var maxUserAvatars = MAX_USER_AVATARS[variant];
  return /*#__PURE__*/jsx("div", _objectSpread(_objectSpread({
    style: {
      display: "inline-block"
    }
  }, rest), {}, {
    className: classNames(CLASSNAME_PREFIXES.AVATAR_CLUSTER, className),
    children: /*#__PURE__*/jsx(Tooltip, _objectSpread(_objectSpread({
      content: getTooltipContent(users.slice(0, MAX_USERNAMES_IN_TOOLTIP[variant]).map(function (_ref3) {
        var name = _ref3.name;
        return name;
      }), t)
    }, tooltipProps), {}, {
      children: /*#__PURE__*/jsx(ClusterWrapper, {
        as: onClick ? "button" : "div",
        onClick: onClick,
        "aria-label": t("AvatarCluster.clusterLabel"),
        children: users.map(function (_ref4, i) {
          var name = _ref4.name,
            src = _ref4.src;
          if (i < maxUserAvatars) {
            return /*#__PURE__*/jsx(AvatarUserContainer, {
              avatarSize: size,
              children: /*#__PURE__*/jsx(AvatarUser, {
                size: size,
                src: src,
                name: name,
                hideToolTip: true
              })
            }, i);
          } else if (i === maxUserAvatars) {
            return /*#__PURE__*/jsx(CountContainer, {
              size: size,
              children: "+".concat(users.length - maxUserAvatars)
            }, i);
          }
        })
      })
    }))
  }));
};
AvatarCluster.displayName = "AvatarCluster";

export { AvatarCluster, ClusterWrapper };
